'use client';

import parse from 'html-react-parser';
import { X } from 'lucide-react';
import { tv } from 'tailwind-variants';
import { cn } from '@/app_lib/utils/utils';

interface TooltipProps {
  showClose?: boolean;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  content: React.ReactNode;
  className?: string;
  variant?: 'default' | 'error';
}

const tooltipStyles = tv({
  base: 'absolute right-0 top-full mt-2 w-80 bg-white rounded shadow-lg p-2 z-10 border-t-4',
  defaultVariants: {
    variant: 'default',
  },
  variants: {
    variant: {
      default: 'border-t-blue-500',
      error: 'border-t-red-500',
    },
  },
});

/**
 * Tooltip component displays a tooltip with a title, close button, and content.
 */
export function tooltip({
  isOpen,
  onClose,
  title,
  content,
  className = '',
  showClose = true,
  variant = 'default',
}: TooltipProps) {
  if (!isOpen) {
    return null;
  }

  return (
    <div className={cn(tooltipStyles({ variant }), className)}>
      {title && (
        <div className='flex items-center justify-between mb-3 gap-2 h-11'>
          <h3
            className='text-sm font-semibold text-neutral-900'
            data-cy='ndr-tooltip-title'
          >
            {title}
          </h3>

          {showClose && (
            <button
              aria-label='Close disclosure'
              className='text-neutral-900 transition-colors p-1 cursor-pointer'
              data-cy='ndr-tooltip-close-button'
              onClick={onClose}
              type='button'
            >
              <X className='w-4 h-4' />
            </button>
          )}
        </div>
      )}
      <div
        className='text-sm text-neutral-900 font-normal leading-relaxed [&_a]:underline [&_a]:text-blue-500'
        data-cy='ndr-tooltip-content'
      >
        {typeof content === 'string' ? parse(content) : content}
      </div>
    </div>
  );
}
